import React, { useCallback, useEffect, useState } from 'react';
import DesignEditor from './DesignEditor';
import './DesignEditor.css';

/**
 * DesignEditorModal – full-screen modal wrapping the DesignEditor.
 *
 * Props:
 *   open                  – boolean, controls visibility
 *   onClose               – called when the user closes without exporting
 *   onExport(base64, json) – called with the final data-URL + fabric JSON
 *   title                 – modal title (default: "Design Editor")
 *   initialImage          – optional base64/URL to pre-load as the canvas background
 *   designJson            – optional saved fabric.js JSON to restore full design
 *   eventImages           – array of { name, src } for "Insert Event Image" in toolbar
 *   preset                – optional { w, h } canvas size
 *   sizeToImage           – size the canvas to the loaded image (product editing)
 */
const DesignEditorModal = ({ open, onClose, onExport, title, initialImage, designJson, eventImages, preset, sizeToImage }) => {
  const [dirty, setDirty] = useState(false);

  // Reset the dirty flag each time the editor opens.
  useEffect(() => { if (open) setDirty(false); }, [open]);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Ai modificări nesalvate. Închizi editorul fără să salvezi?')) {
      return;
    }
    onClose();
  }, [dirty, onClose]);

  // Escape closes (with the same unsaved-changes guard).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); requestClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, requestClose]);

  if (!open) return null;

  return (
    // No backdrop-click-to-close: a stray click outside the panel must not
    // silently discard an in-progress edit. Close via the ✕ button or Escape.
    <div className="de-modal-overlay">
      <div className="de-modal">
        <div className="de-modal-header">
          <h3>{title || 'Design Editor'}</h3>
          <button className="de-modal-close" onClick={requestClose}>✕</button>
        </div>
        <DesignEditor
          initialImage={initialImage}
          designJson={designJson}
          eventImages={eventImages}
          preset={preset}
          sizeToImage={sizeToImage}
          onDirtyChange={setDirty}
          onExport={(dataUrl, json) => {
            onExport(dataUrl, json);
            onClose();
          }}
        />
      </div>
    </div>
  );
};

export default DesignEditorModal;
